import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, createBlock as _createBlock, withDirectives as _withDirectives, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, Teleport as _Teleport, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-542a7aac"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "select-tags" }
const _hoisted_2 = ["id", "data-expanded"]
const _hoisted_3 = { class: "controls" }
const _hoisted_4 = /*#__PURE__*/_createTextVNode(" " + /*#__PURE__*/_toDisplayString(" "))
const _hoisted_5 = ["id", "aria-labelledby", "aria-activedescendant"]
const _hoisted_6 = /*#__PURE__*/_createTextVNode("Loading results")
const _hoisted_7 = /*#__PURE__*/_createTextVNode("Error loading results")
const _hoisted_8 = ["id", "data-highlighted", "onClick", "onMouseenterCapture"]
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = {
  key: 0,
  class: "option-info truncate"
}
const _hoisted_11 = {
  key: 0,
  class: "description"
}

import { ref, computed, watch, nextTick } from "vue";
import { uniqueId, isEqual, uniqBy } from "lodash";
import { Option, Options, OptionsFunc } from "./AppSelectTags";
import { wrap } from "@/util/math";
import { snackbar } from "./TheSnackbar";
import { sleep } from "@/util/debug";
import { useFloating, useQuery } from "@/util/composables";
import AppInput from "./AppInput.vue";

interface Props {
  /** two-way bound selected items state */
  modelValue: Options;
  /** name of the field */
  name: string;
  /** placeholder string when nothing typed in */
  placeholder?: string;
  /** async function that returns list of options to show */
  options: OptionsFunc;
  /** tooltip when hovering input */
  tooltip?: string;
  /** description to show below box */
  description?: string;
}

interface Emits {
  /** two-way bound selected items state */
  (event: "update:modelValue", value: Options): void;
  /** when an option's spreadOptions func has been called */
  (event: "spreadOptions", option: Option, options: Options): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AppSelectTags',
  props: {
    modelValue: null,
    name: null,
    placeholder: null,
    options: null,
    tooltip: null,
    description: null
  },
  emits: ["update:modelValue", "spreadOptions"],
  setup(__props: any, { emit }: { emit: ({
  /** two-way bound selected items state */
  (event: "update:modelValue", value: Options): void;
  /** when an option's spreadOptions func has been called */
  (event: "spreadOptions", option: Option, options: Options): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  /** two-way bound selected items state */
  modelValue: Options;
  /** name of the field */
  name: string;
  /** placeholder string when nothing typed in */
  placeholder?: string;
  /** async function that returns list of options to show */
  options: OptionsFunc;
  /** tooltip when hovering input */
  tooltip?: string;
  /** description to show below box */
  description?: string;
}





/** unique id for instance of component */
const id = ref(uniqueId());
/** array of selected options */
const selected = ref<Options>([]);
/** currently searched text */
const search = ref("");
/** index of option that is highlighted */
const highlighted = ref(0);
/** whether input box focused and dropdown expanded */
const expanded = ref(false);

/** open results dropdown */
async function open() {
  expanded.value = true;
  highlighted.value = -1;
  await getResults();
}

/** close results dropdown */
function close() {
  expanded.value = false;
  search.value = "";
  results.value = { options: [] };
  highlighted.value = -1;
}

/** when user presses key in input */
function onKeydown(event: KeyboardEvent) {
  /** arrow/home/end keys */
  if (["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) {
    /** prevent page scroll */
    event.preventDefault();

    /** move value up/down */
    let index = highlighted.value;
    if (event.key === "ArrowUp") index--;
    if (event.key === "ArrowDown") index++;
    if (event.key === "Home") index = 0;
    if (event.key === "End") index = availableResults.value.length - 1;

    /** update highlighted, wrapping beyond 0 or results length */
    highlighted.value = wrap(index, 0, availableResults.value.length - 1);
  }

  /** backspace key to deselect last-selected option */
  if (event.key === "Backspace") {
    if (search.value === "") deselect();
  }

  /** enter key to de/select highlighted result */
  if (event.key === "Enter" && highlighted.value >= 0) {
    event.preventDefault();
    select(availableResults.value[highlighted.value]);
  }

  /** esc key to close dropdown */
  if (event.key === "Escape") close();
}

/** when user pastes text */
async function onPaste() {
  /**
   * wait for pasted value to take effect but don't use nextTick because by then
   * search.value will be reset
   */
  await sleep();
  /** immediately auto-accept results */
  await getResults();
}

/** select an option or array of options */
async function select(options: Option | Options) {
  /** make array if single option */
  if (!Array.isArray(options)) options = [options];

  /** array of options to select */
  const toSelect: Options = [];

  for (const option of options) {
    /** run func to get options to select */
    if (option.spreadOptions) {
      const options = await option.spreadOptions();
      toSelect.push(...options);
      /**
       * notify parent that dynamic options were added. provide option selected
       * and options added.
       */
      emit("spreadOptions", option, options);
    } else toSelect.push(option);
    /** otherwise just select option */
  }

  /** select options */
  selected.value.push(...toSelect);
}

/** deselect a specific option or last-selected option */
function deselect(option?: Option) {
  if (option)
    selected.value = selected.value.filter((model) => model.id !== option.id);
  else selected.value.pop();
}

/** clear all selected */
function clear() {
  selected.value = [];
}

/** copy selected ids to clipboard */
async function copy() {
  await window.navigator.clipboard?.writeText(
    selected.value.map(({ id }) => id).join(",")
  );
  snackbar(`Copied ${selected.value.length} values`);
}

const {
  query: getResults,
  data: results,
  isLoading,
  isError,
} = useQuery(
  /** get list of results */
  async function () {
    /** reset highlighted */
    highlighted.value = 0;

    /** get results */
    return await props.options(search.value);
  },

  /** default value */
  { options: [] },

  /** on success */
  (result) => {
    if (result.autoAccept) {
      /** auto select */
      select(result.options);

      /** display message */
      if (result.message) snackbar(result.message);

      /** reset */
      close();
    }
  }
);

/** list of unselected results to show */
const availableResults = computed(() =>
  results.value.autoAccept
    ? []
    : results.value.options.filter(
        (option) => !selected.value.find((model) => model.id === option.id)
      )
);

/** target element */
const target = ref();
/** dropdown element */
const dropdown = ref();
/** get dropdown position */
const { calculate, style } = useFloating(target, dropdown, true);
/** recompute position when length of results changes */
watch([expanded, availableResults], async () => {
  await nextTick();
  if (expanded.value) calculate();
});

/** when model changes */
watch(
  () => props.modelValue,
  () => {
    /** avoid infinite rerenders */
    if (!isEqual(selected.value, props.modelValue))
      /** update (de-duplicated) selected value */
      selected.value = uniqBy(props.modelValue, "id");
  },
  { deep: true, immediate: true }
);

/** when selected value changes */
watch(
  selected,
  () => {
    /** emit (deduplicated) updated model */
    emit("update:modelValue", uniqBy(selected.value, "id"));
  },
  { deep: true }
);

/** when highlighted index changes */
watch(highlighted, () => {
  /** scroll to highlighted in dropdown */
  document
    .querySelector(`#option-${id.value}-${highlighted.value} > *`)
    ?.scrollIntoView({ block: "nearest" });
});

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_AppFlex = _resolveComponent("AppFlex")!
  const _component_AppStatus = _resolveComponent("AppStatus")!
  const _component_AppIcon = _resolveComponent("AppIcon")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      id: `select-${id.value}`,
      ref_key: "target",
      ref: target,
      class: "box",
      "data-expanded": expanded.value
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selected.value, (option, index) => {
        return _withDirectives((_openBlock(), _createBlock(_component_AppButton, {
          key: index,
          design: "circle",
          text: option.name || option.id,
          icon: "xmark",
          class: "selected",
          "aria-label": `Deselect ${option.id}`,
          onClick: ($event: any) => (deselect(option))
        }, null, 8, ["text", "aria-label", "onClick"])), [
          [_directive_tooltip, `Deselect ${option.id}`]
        ])
      }), 128)),
      _createVNode(_component_AppFlex, { "h-align": "right" }, {
        default: _withCtx(() => [
          _withDirectives(_createVNode(AppInput, {
            modelValue: search.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
            placeholder: __props.placeholder,
            class: "input",
            role: "combobox",
            "aria-label": __props.name,
            "aria-expanded": !!_unref(results).options.length,
            "aria-controls": `list-${id.value}`,
            "aria-haspopup": "listbox",
            "aria-autocomplete": "list",
            onFocus: open,
            onBlur: close,
            onDebounce: _unref(getResults),
            onKeydown: onKeydown,
            onPaste: onPaste
          }, null, 8, ["modelValue", "placeholder", "aria-label", "aria-expanded", "aria-controls", "onDebounce"]), [
            [_directive_tooltip, { content: __props.tooltip, offset: [20, 20] }]
          ]),
          _createElementVNode("div", _hoisted_3, [
            _withDirectives(_createVNode(_component_AppButton, {
              design: "small",
              icon: "copy",
              onClick: copy
            }, null, 512), [
              [_directive_tooltip, `Copy selected values`]
            ]),
            _hoisted_4,
            _withDirectives(_createVNode(_component_AppButton, {
              design: "small",
              icon: "times",
              onClick: clear
            }, null, 512), [
              [_directive_tooltip, `Clear selected values`]
            ])
          ])
        ]),
        _: 1
      })
    ], 8, _hoisted_2),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (expanded.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            id: `list-${id.value}`,
            ref_key: "dropdown",
            ref: dropdown,
            class: "list",
            role: "listbox",
            tabindex: "0",
            "aria-labelledby": `select-${id.value}`,
            "aria-activedescendant": 
          _unref(results).options.length && highlighted.value >= 0
            ? `option-${id.value}-${highlighted.value}`
            : undefined
        ,
            style: _normalizeStyle(_unref(style))
          }, [
            (_unref(isLoading))
              ? (_openBlock(), _createBlock(_component_AppStatus, {
                  key: 0,
                  code: "loading",
                  role: "option"
                }, {
                  default: _withCtx(() => [
                    _hoisted_6
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (_unref(isError))
              ? (_openBlock(), _createBlock(_component_AppStatus, {
                  key: 1,
                  code: "error",
                  role: "option"
                }, {
                  default: _withCtx(() => [
                    _hoisted_7
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(availableResults), (option, index) => {
              return _withDirectives((_openBlock(), _createElementBlock("div", {
                id: `option-${id.value}-${index}`,
                key: index,
                class: "option",
                role: "option",
                "aria-selected": true,
                "data-highlighted": index === highlighted.value,
                tabindex: "0",
                onClick: ($event: any) => (select(option)),
                onMouseenterCapture: ($event: any) => (highlighted.value = index),
                onMousedown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
                onFocusin: _cache[2] || (_cache[2] = () => null),
                onKeydown: _cache[3] || (_cache[3] = () => null)
              }, [
                _createElementVNode("span", null, [
                  (option.icon)
                    ? (_openBlock(), _createBlock(_component_AppIcon, {
                        key: 0,
                        icon: option.icon,
                        class: "option-icon"
                      }, null, 8, ["icon"]))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("span", {
                  class: "option-label truncate",
                  innerHTML: option.highlight || option.name || option.id
                }, null, 8, _hoisted_9),
                (option.info)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(option.info), 1))
                  : _createCommentVNode("", true)
              ], 40, _hoisted_8)), [
                [_directive_tooltip, option.tooltip]
              ])
            }), 128))
          ], 12, _hoisted_5))
        : _createCommentVNode("", true)
    ])),
    (__props.description)
      ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(__props.description), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})